// How current a cached live-data view is (Apps, dashboards, living Results): the one quiet line and Refresh in the
// surface's top bar. Namespace `view-freshness`; copy by DESIGN. `{time}` is the localized relative time of the
// actual successful fetch ("4 minutes ago", "yesterday", or a date).
// The sync keys say the same for an App that syncs a connected source into its own tables: `{time}` is its last
// verified sync, and Sync now runs that sync. `syncUnknown` is a sync whose outcome is not known yet (it may have run).
import type { NamespaceCatalog } from '@/i18n/ns';

const en = {
  updated: 'Updated {time}',
  justNow: 'Updated just now',
  updating: 'Updating…',
  failed: 'Couldn’t update',
  refresh: 'Refresh',
  synced: 'Last synced {time}',
  syncedJustNow: 'Synced just now',
  syncing: 'Syncing…',
  syncFailed: 'Couldn’t sync',
  syncUnknown: 'Sync not confirmed',
  notSynced: 'Not synced yet',
  syncNow: 'Sync now',
  syncUnconfirmed: 'This sync couldn’t be confirmed yet. The last good entries stay as they are.',
};
export type ViewFreshnessKey = keyof typeof en;
type Copy = Record<ViewFreshnessKey, string>;

const bg: Copy = {
  updated: 'Обновено {time}',
  justNow: 'Обновено току-що',
  updating: 'Обновяване…',
  failed: 'Неуспешно обновяване',
  refresh: 'Обнови',
  synced: 'Последна синхронизация {time}',
  syncedJustNow: 'Синхронизирано току-що',
  syncing: 'Синхронизиране…',
  syncFailed: 'Неуспешна синхронизация',
  syncUnknown: 'Синхронизацията не е потвърдена',
  notSynced: 'Още не е синхронизирано',
  syncNow: 'Синхронизирай сега',
  syncUnconfirmed: 'Синхронизацията още не е потвърдена. Последните потвърдени записи остават.',
};
const cs: Copy = {
  updated: 'Aktualizováno {time}',
  justNow: 'Aktualizováno právě teď',
  updating: 'Aktualizace…',
  failed: 'Aktualizace se nezdařila',
  refresh: 'Obnovit',
  synced: 'Naposledy synchronizováno {time}',
  syncedJustNow: 'Synchronizováno právě teď',
  syncing: 'Synchronizace…',
  syncFailed: 'Synchronizace se nezdařila',
  syncUnknown: 'Synchronizace nepotvrzena',
  notSynced: 'Zatím nesynchronizováno',
  syncNow: 'Synchronizovat',
  syncUnconfirmed: 'Synchronizaci zatím nelze potvrdit. Poslední platné záznamy zůstávají.',
};
const da: Copy = {
  updated: 'Opdateret {time}',
  justNow: 'Opdateret lige nu',
  updating: 'Opdaterer…',
  failed: 'Kunne ikke opdatere',
  refresh: 'Opdater',
  synced: 'Sidst synkroniseret {time}',
  syncedJustNow: 'Synkroniseret lige nu',
  syncing: 'Synkroniserer…',
  syncFailed: 'Kunne ikke synkronisere',
  syncUnknown: 'Synkronisering ikke bekræftet',
  notSynced: 'Ikke synkroniseret endnu',
  syncNow: 'Synkroniser nu',
  syncUnconfirmed: 'Synkroniseringen kunne ikke bekræftes endnu. De seneste gyldige poster bliver.',
};
const de: Copy = {
  updated: 'Aktualisiert {time}',
  justNow: 'Gerade aktualisiert',
  updating: 'Wird aktualisiert…',
  failed: 'Aktualisierung fehlgeschlagen',
  refresh: 'Aktualisieren',
  synced: 'Zuletzt synchronisiert {time}',
  syncedJustNow: 'Gerade synchronisiert',
  syncing: 'Wird synchronisiert…',
  syncFailed: 'Synchronisierung fehlgeschlagen',
  syncUnknown: 'Synchronisierung nicht bestätigt',
  notSynced: 'Noch nicht synchronisiert',
  syncNow: 'Jetzt synchronisieren',
  syncUnconfirmed:
    'Die Synchronisierung konnte noch nicht bestätigt werden. Die letzten gültigen Einträge bleiben erhalten.',
};
const el: Copy = {
  updated: 'Ενημερώθηκε {time}',
  justNow: 'Ενημερώθηκε μόλις τώρα',
  updating: 'Ενημέρωση…',
  failed: 'Η ενημέρωση απέτυχε',
  refresh: 'Ανανέωση',
  synced: 'Τελευταίος συγχρονισμός {time}',
  syncedJustNow: 'Συγχρονίστηκε μόλις τώρα',
  syncing: 'Συγχρονισμός…',
  syncFailed: 'Ο συγχρονισμός απέτυχε',
  syncUnknown: 'Ο συγχρονισμός δεν επιβεβαιώθηκε',
  notSynced: 'Δεν έχει συγχρονιστεί ακόμη',
  syncNow: 'Συγχρονισμός τώρα',
  syncUnconfirmed:
    'Ο συγχρονισμός δεν έχει επιβεβαιωθεί ακόμη. Οι τελευταίες έγκυρες καταχωρίσεις παραμένουν.',
};
const es: Copy = {
  updated: 'Actualizado {time}',
  justNow: 'Actualizado ahora mismo',
  updating: 'Actualizando…',
  failed: 'No se pudo actualizar',
  refresh: 'Actualizar',
  synced: 'Última sincronización {time}',
  syncedJustNow: 'Sincronizado ahora mismo',
  syncing: 'Sincronizando…',
  syncFailed: 'No se pudo sincronizar',
  syncUnknown: 'Sincronización sin confirmar',
  notSynced: 'Aún sin sincronizar',
  syncNow: 'Sincronizar ahora',
  syncUnconfirmed:
    'Aún no se ha podido confirmar la sincronización. Las últimas entradas válidas se mantienen.',
};
const et: Copy = {
  updated: 'Uuendatud {time}',
  justNow: 'Uuendatud just praegu',
  updating: 'Uuendamine…',
  failed: 'Uuendamine ebaõnnestus',
  refresh: 'Värskenda',
  synced: 'Viimati sünkroonitud {time}',
  syncedJustNow: 'Just sünkroonitud',
  syncing: 'Sünkroonimine…',
  syncFailed: 'Sünkroonimine ebaõnnestus',
  syncUnknown: 'Sünkroonimine kinnitamata',
  notSynced: 'Pole veel sünkroonitud',
  syncNow: 'Sünkrooni kohe',
  syncUnconfirmed:
    'Sünkroonimist ei õnnestunud veel kinnitada. Viimased kehtivad kirjed jäävad alles.',
};
const fi: Copy = {
  updated: 'Päivitetty {time}',
  justNow: 'Päivitetty juuri nyt',
  updating: 'Päivitetään…',
  failed: 'Päivitys epäonnistui',
  refresh: 'Päivitä',
  synced: 'Synkronoitu viimeksi {time}',
  syncedJustNow: 'Synkronoitu juuri nyt',
  syncing: 'Synkronoidaan…',
  syncFailed: 'Synkronointi epäonnistui',
  syncUnknown: 'Synkronointia ei vahvistettu',
  notSynced: 'Ei vielä synkronoitu',
  syncNow: 'Synkronoi nyt',
  syncUnconfirmed:
    'Synkronointia ei voitu vielä vahvistaa. Viimeisimmät kelvolliset tiedot säilyvät.',
};
const fr: Copy = {
  updated: 'Mis à jour {time}',
  justNow: 'Mis à jour à l’instant',
  updating: 'Mise à jour…',
  failed: 'Mise à jour impossible',
  refresh: 'Actualiser',
  synced: 'Dernière synchronisation {time}',
  syncedJustNow: 'Synchronisé à l’instant',
  syncing: 'Synchronisation…',
  syncFailed: 'Échec de la synchronisation',
  syncUnknown: 'Synchronisation non confirmée',
  notSynced: 'Pas encore synchronisé',
  syncNow: 'Synchroniser',
  syncUnconfirmed:
    'La synchronisation n’a pas encore pu être confirmée. Les dernières entrées valides sont conservées.',
};
const ga: Copy = {
  updated: 'Nuashonraithe {time}',
  justNow: 'Nuashonraithe díreach anois',
  updating: 'Á nuashonrú…',
  failed: 'Níorbh fhéidir nuashonrú',
  refresh: 'Athnuaigh',
  synced: 'Sioncronaithe go deireanach {time}',
  syncedJustNow: 'Sioncronaithe díreach anois',
  syncing: 'Á shioncronú…',
  syncFailed: 'Níorbh fhéidir sioncronú',
  syncUnknown: 'Sioncronú gan deimhniú',
  notSynced: 'Gan sioncronú fós',
  syncNow: 'Sioncronaigh anois',
  syncUnconfirmed:
    'Níorbh fhéidir an sioncronú a dheimhniú fós. Fanann na hiontrálacha bailí is déanaí mar atá.',
};
const hr: Copy = {
  updated: 'Ažurirano {time}',
  justNow: 'Upravo ažurirano',
  updating: 'Ažuriranje…',
  failed: 'Ažuriranje nije uspjelo',
  refresh: 'Osvježi',
  synced: 'Zadnja sinkronizacija {time}',
  syncedJustNow: 'Upravo sinkronizirano',
  syncing: 'Sinkronizacija…',
  syncFailed: 'Sinkronizacija nije uspjela',
  syncUnknown: 'Sinkronizacija nije potvrđena',
  notSynced: 'Još nije sinkronizirano',
  syncNow: 'Sinkroniziraj sada',
  syncUnconfirmed: 'Sinkronizacija još nije potvrđena. Zadnji ispravni unosi ostaju.',
};
const hu: Copy = {
  updated: 'Frissítve: {time}',
  justNow: 'Épp most frissítve',
  updating: 'Frissítés…',
  failed: 'Nem sikerült frissíteni',
  refresh: 'Frissítés',
  synced: 'Utoljára szinkronizálva {time}',
  syncedJustNow: 'Épp most szinkronizálva',
  syncing: 'Szinkronizálás…',
  syncFailed: 'A szinkronizálás nem sikerült',
  syncUnknown: 'A szinkronizálás nincs megerősítve',
  notSynced: 'Még nincs szinkronizálva',
  syncNow: 'Szinkronizálás most',
  syncUnconfirmed:
    'A szinkronizálást még nem sikerült megerősíteni. Az utolsó érvényes bejegyzések megmaradnak.',
};
const it: Copy = {
  updated: 'Aggiornato {time}',
  justNow: 'Aggiornato ora',
  updating: 'Aggiornamento…',
  failed: 'Impossibile aggiornare',
  refresh: 'Aggiorna',
  synced: 'Ultima sincronizzazione {time}',
  syncedJustNow: 'Sincronizzato ora',
  syncing: 'Sincronizzazione…',
  syncFailed: 'Sincronizzazione non riuscita',
  syncUnknown: 'Sincronizzazione non confermata',
  notSynced: 'Non ancora sincronizzato',
  syncNow: 'Sincronizza ora',
  syncUnconfirmed:
    'Non è ancora stato possibile confermare la sincronizzazione. Le ultime voci valide restano invariate.',
};
const lt: Copy = {
  updated: 'Atnaujinta {time}',
  justNow: 'Atnaujinta ką tik',
  updating: 'Atnaujinama…',
  failed: 'Nepavyko atnaujinti',
  refresh: 'Atnaujinti',
  synced: 'Paskutinį kartą sinchronizuota {time}',
  syncedJustNow: 'Ką tik sinchronizuota',
  syncing: 'Sinchronizuojama…',
  syncFailed: 'Nepavyko sinchronizuoti',
  syncUnknown: 'Sinchronizavimas nepatvirtintas',
  notSynced: 'Dar nesinchronizuota',
  syncNow: 'Sinchronizuoti dabar',
  syncUnconfirmed: 'Sinchronizavimo dar nepavyko patvirtinti. Paskutiniai tinkami įrašai lieka.',
};
const lv: Copy = {
  updated: 'Atjaunināts {time}',
  justNow: 'Atjaunināts tikko',
  updating: 'Atjaunina…',
  failed: 'Neizdevās atjaunināt',
  refresh: 'Atsvaidzināt',
  synced: 'Pēdējā sinhronizācija {time}',
  syncedJustNow: 'Tikko sinhronizēts',
  syncing: 'Notiek sinhronizācija…',
  syncFailed: 'Neizdevās sinhronizēt',
  syncUnknown: 'Sinhronizācija nav apstiprināta',
  notSynced: 'Vēl nav sinhronizēts',
  syncNow: 'Sinhronizēt tagad',
  syncUnconfirmed: 'Sinhronizāciju vēl neizdevās apstiprināt. Pēdējie derīgie ieraksti paliek.',
};
const mt: Copy = {
  updated: 'Aġġornat {time}',
  justNow: 'Għadu kif ġie aġġornat',
  updating: 'Qed jiġi aġġornat…',
  failed: 'Ma setax jiġi aġġornat',
  refresh: 'Aġġorna',
  synced: 'L-aħħar sinkronizzazzjoni {time}',
  syncedJustNow: 'Sinkronizzat issa',
  syncing: 'Qed jissinkronizza…',
  syncFailed: 'Is-sinkronizzazzjoni ma rnexxietx',
  syncUnknown: 'Is-sinkronizzazzjoni mhix ikkonfermata',
  notSynced: 'Għadu mhux sinkronizzat',
  syncNow: 'Issinkronizza issa',
  syncUnconfirmed:
    'Is-sinkronizzazzjoni għadha ma ġietx ikkonfermata. L-aħħar entrati tajbin jibqgħu kif inhuma.',
};
const nl: Copy = {
  updated: 'Bijgewerkt {time}',
  justNow: 'Zojuist bijgewerkt',
  updating: 'Bijwerken…',
  failed: 'Bijwerken mislukt',
  refresh: 'Vernieuwen',
  synced: 'Laatst gesynchroniseerd {time}',
  syncedJustNow: 'Zojuist gesynchroniseerd',
  syncing: 'Synchroniseren…',
  syncFailed: 'Synchroniseren mislukt',
  syncUnknown: 'Synchronisatie niet bevestigd',
  notSynced: 'Nog niet gesynchroniseerd',
  syncNow: 'Nu synchroniseren',
  syncUnconfirmed:
    'De synchronisatie kon nog niet worden bevestigd. De laatste geldige gegevens blijven staan.',
};
const pl: Copy = {
  updated: 'Zaktualizowano {time}',
  justNow: 'Zaktualizowano przed chwilą',
  updating: 'Aktualizowanie…',
  failed: 'Nie udało się zaktualizować',
  refresh: 'Odśwież',
  synced: 'Ostatnia synchronizacja {time}',
  syncedJustNow: 'Zsynchronizowano przed chwilą',
  syncing: 'Synchronizowanie…',
  syncFailed: 'Nie udało się zsynchronizować',
  syncUnknown: 'Synchronizacja niepotwierdzona',
  notSynced: 'Jeszcze nie zsynchronizowano',
  syncNow: 'Synchronizuj teraz',
  syncUnconfirmed:
    'Nie udało się jeszcze potwierdzić synchronizacji. Ostatnie poprawne wpisy pozostają bez zmian.',
};
const pt: Copy = {
  updated: 'Atualizado {time}',
  justNow: 'Atualizado agora mesmo',
  updating: 'A atualizar…',
  failed: 'Não foi possível atualizar',
  refresh: 'Atualizar',
  synced: 'Última sincronização {time}',
  syncedJustNow: 'Sincronizado agora mesmo',
  syncing: 'A sincronizar…',
  syncFailed: 'Não foi possível sincronizar',
  syncUnknown: 'Sincronização não confirmada',
  notSynced: 'Ainda não sincronizado',
  syncNow: 'Sincronizar agora',
  syncUnconfirmed:
    'Ainda não foi possível confirmar a sincronização. As últimas entradas válidas mantêm-se.',
};
const ro: Copy = {
  updated: 'Actualizat {time}',
  justNow: 'Actualizat chiar acum',
  updating: 'Se actualizează…',
  failed: 'Actualizarea nu a reușit',
  refresh: 'Reîmprospătează',
  synced: 'Ultima sincronizare {time}',
  syncedJustNow: 'Sincronizat chiar acum',
  syncing: 'Se sincronizează…',
  syncFailed: 'Sincronizarea a eșuat',
  syncUnknown: 'Sincronizare neconfirmată',
  notSynced: 'Încă nesincronizat',
  syncNow: 'Sincronizează acum',
  syncUnconfirmed:
    'Sincronizarea nu a putut fi confirmată încă. Ultimele intrări valide rămân neschimbate.',
};
const sk: Copy = {
  updated: 'Aktualizované {time}',
  justNow: 'Aktualizované práve teraz',
  updating: 'Aktualizuje sa…',
  failed: 'Aktualizácia zlyhala',
  refresh: 'Obnoviť',
  synced: 'Naposledy synchronizované {time}',
  syncedJustNow: 'Synchronizované práve teraz',
  syncing: 'Synchronizácia…',
  syncFailed: 'Synchronizácia zlyhala',
  syncUnknown: 'Synchronizácia nepotvrdená',
  notSynced: 'Zatiaľ nesynchronizované',
  syncNow: 'Synchronizovať',
  syncUnconfirmed:
    'Synchronizáciu zatiaľ nie je možné potvrdiť. Posledné platné záznamy zostávajú.',
};
const sl: Copy = {
  updated: 'Posodobljeno {time}',
  justNow: 'Pravkar posodobljeno',
  updating: 'Posodabljanje…',
  failed: 'Posodobitev ni uspela',
  refresh: 'Osveži',
  synced: 'Zadnja sinhronizacija {time}',
  syncedJustNow: 'Pravkar sinhronizirano',
  syncing: 'Sinhronizacija…',
  syncFailed: 'Sinhronizacija ni uspela',
  syncUnknown: 'Sinhronizacija ni potrjena',
  notSynced: 'Še ni sinhronizirano',
  syncNow: 'Sinhroniziraj zdaj',
  syncUnconfirmed: 'Sinhronizacije še ni bilo mogoče potrditi. Zadnji veljavni vnosi ostanejo.',
};
const sv: Copy = {
  updated: 'Uppdaterad {time}',
  justNow: 'Uppdaterad nyss',
  updating: 'Uppdaterar…',
  failed: 'Kunde inte uppdatera',
  refresh: 'Uppdatera',
  synced: 'Senast synkad {time}',
  syncedJustNow: 'Synkad nyss',
  syncing: 'Synkar…',
  syncFailed: 'Kunde inte synka',
  syncUnknown: 'Synken är inte bekräftad',
  notSynced: 'Inte synkad än',
  syncNow: 'Synka nu',
  syncUnconfirmed: 'Synken kunde inte bekräftas än. De senaste bekräftade posterna ligger kvar.',
};

export const viewFreshnessCopy: NamespaceCatalog<ViewFreshnessKey> = {
  en,
  bg,
  cs,
  da,
  de,
  el,
  es,
  et,
  fi,
  fr,
  ga,
  hr,
  hu,
  it,
  lt,
  lv,
  mt,
  nl,
  pl,
  pt,
  ro,
  sk,
  sl,
  sv,
};
export default viewFreshnessCopy;
